// v1.28.28 — Single source of truth for every user-visible date in MICM.
//
// FORMAT CONTRACT (per user policy — locked in v1.28.28):
//   * On-screen and download content dates → DD-MM-YYYY (hyphens)
//   * Timestamps (created / updated / uploaded) → DD-MM-YYYY HH:mm (24h)
//   * API payloads + <input type="date"> stay ISO YYYY-MM-DD (never surfaced to user)
//
// Every component that renders a date to the user MUST go through
// `formatDate()` (aliases: `dmY`, `dmYhm`). Do NOT call `toLocaleDateString`,
// `toLocaleString`, or handwritten `${y}-${m}-${d}` templates in JSX —
// centralising here means future format changes are a one-file swap.
//
// Two input shapes are supported:
//   - ISO date string "YYYY-MM-DD" (Mongo payment_date, business_date, etc.)
//   - Full ISO timestamp "YYYY-MM-DDTHH:mm:ss…" (Date-constructor friendly)
//   - Date instance
// Blank/invalid inputs return "" so components can render safely.

const _pad = (n) => String(n).padStart(2, "0");

/** Format any date-like value as DD-MM-YYYY. Alias: `dmY`. */
export const formatDate = (val) => {
    if (val == null || val === "") return "";
    const s = String(val);
    // Pure ISO date "YYYY-MM-DD" — the common case for payment_date etc.
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    return `${_pad(d.getDate())}-${_pad(d.getMonth() + 1)}-${d.getFullYear()}`;
};

/** Format a full timestamp as `DD-MM-YYYY HH:mm` (24h). Alias: `dmYhm`. */
export const formatDateTime = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "";
    return `${_pad(d.getDate())}-${_pad(d.getMonth() + 1)}-${d.getFullYear()} ${_pad(d.getHours())}:${_pad(d.getMinutes())}`;
};

/** v1.29.15 — Format a full timestamp with seconds as `DD-MM-YYYY HH:mm:ss` (24h).
 *  Used exclusively in tooltip contexts where the user asked for extra
 *  precision (e.g. Created / Last edited timestamp popovers on ledger rows).
 *  Alias: `dmYhms`. */
export const formatDateTimeSeconds = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "";
    return `${_pad(d.getDate())}-${_pad(d.getMonth() + 1)}-${d.getFullYear()} ${_pad(d.getHours())}:${_pad(d.getMinutes())}:${_pad(d.getSeconds())}`;
};

// Legacy aliases — every existing import site keeps working.
export const dmY = formatDate;
export const dmYhm = formatDateTime;
export const dmYhms = formatDateTimeSeconds;
